<template>
  <section class="panel notice-panel">
    <div class="panel-title notice-title-row">
      <span>긴급 공지</span>
      <span v-if="emergencyNotices.length > 1" class="notice-counter">
        {{ currentNoticeIndex + 1 }} / {{ emergencyNotices.length }}
      </span>
    </div>

    <div v-if="currentEmergencyNotice" class="notice-slider">
      <button
        v-if="emergencyNotices.length > 1"
        class="slide-btn prev-btn notice-nav-btn"
        @click="prevNotice"
        type="button"
      >
        <i class="fa-solid fa-chevron-left"></i>
      </button>

      <div class="notice-content">
        <div class="notice-text" @click="openNotice(currentEmergencyNotice)">
          {{ currentEmergencyNotice.content }}
        </div>

        <div
          v-if="currentEmergencyNotice.siteNames && currentEmergencyNotice.siteNames.length"
          class="notice-sites"
        >
          {{ currentEmergencyNotice.siteNames.join(', ') }}
        </div>

        <div v-if="currentEmergencyNotice.createdAt" class="notice-created-at">
          {{ formatNoticeDate(currentEmergencyNotice.createdAt) }}
        </div>
      </div>

      <button
        v-if="emergencyNotices.length > 1"
        class="slide-btn next-btn notice-nav-btn"
        @click="nextNotice"
        type="button"
      >
        <i class="fa-solid fa-chevron-right"></i>
      </button>
    </div>

    <div v-else class="notice-empty">
      등록된 긴급 공지가 없습니다.
    </div>

    <div v-if="emergencyNotices.length > 1" class="slide-indicators notice-indicators">
      <button
        v-for="(notice, index) in emergencyNotices"
        :key="notice.id || index"
        class="indicator"
        :class="{ active: index === currentNoticeIndex }"
        @click="goToNotice(index)"
        type="button"
      />
    </div>
  </section>

  <teleport to="body">
    <div v-if="selectedNotice" class="notice-modal">
      <div class="notice-modal-content">

        <button class="close-btn" @click="closeNotice">✕</button>

        <div class="notice-modal-title">
          긴급 공지
        </div>

        <div class="notice-modal-text">
          {{ selectedNotice.content }}
        </div>

        <div class="notice-modal-meta">
          {{ formatNoticeDate(selectedNotice.createdAt) }}
        </div>

      </div>
    </div>
  </teleport>
</template>

<script>
export default {
  name: 'NoticePanel',

  props: {
    emergencyNotices: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      currentNoticeIndex: 0,
      noticeTimer: null,
      selectedNotice: null
    }
  },

  computed: {
    currentEmergencyNotice() {
      if (!this.emergencyNotices.length) return null
      return this.emergencyNotices[this.currentNoticeIndex] || null
    }
  },

  watch: {
    emergencyNotices: {
      immediate: true,
      deep: true,
      handler() {
        this.currentNoticeIndex = 0
        this.restartNoticeSlide()
      }
    }
  },

  beforeUnmount() {
    this.clearNoticeTimer()
  },

  methods: {
    clearNoticeTimer() {
      if (this.noticeTimer) {
        clearInterval(this.noticeTimer)
        this.noticeTimer = null
      }
    },

    prevNotice() {
      if (!this.emergencyNotices.length) return

      this.currentNoticeIndex =
        this.currentNoticeIndex === 0
          ? this.emergencyNotices.length - 1
          : this.currentNoticeIndex - 1

      this.restartNoticeSlide()
    },

    nextNotice() {
      if (!this.emergencyNotices.length) return

      this.currentNoticeIndex =
        this.currentNoticeIndex === this.emergencyNotices.length - 1
          ? 0
          : this.currentNoticeIndex + 1
    },

    goToNotice(index) {
      this.currentNoticeIndex = index
      this.restartNoticeSlide()
    },

    startNoticeSlide() {
      this.clearNoticeTimer()

      if (this.emergencyNotices.length <= 1) return

      this.noticeTimer = setInterval(() => {
        this.nextNotice()
      }, 4000)
    },

    restartNoticeSlide() {
      this.clearNoticeTimer()
      this.startNoticeSlide()
    },

    formatNoticeDate(value) {
      if (!value) return ''

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value

      const yyyy = date.getFullYear()
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mi = String(date.getMinutes()).padStart(2, '0')

      return `${yyyy}.${mm}.${dd} ${hh}:${mi}`
    },

    openNotice(notice) {
      this.selectedNotice = notice
    },
    closeNotice() {
      this.selectedNotice = null
    },
  }
}
</script>

<style scoped>
/* ===================== 기본 카드 ===================== */
.panel {
  background: #ffffff;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.panel-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text-main);
}

/* ===================== 타이틀 ===================== */
.notice-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.notice-counter {
  font-size: 12px;
  color: var(--color-text-sub);
}

/* ===================== 슬라이더 ===================== */
.notice-slider {
  display: flex;
  align-items: stretch;
  gap: 10px;
  margin-top: 12px;
}

/* ===================== 공지 카드 ===================== */
.notice-content {
  flex: 1;
  min-width: 0;

  background: #f8fafc;
  border: 1px solid #e9edf3;
  border-radius: 12px;
  padding: 12px;

  overflow: hidden; /* 🔥 깨짐 방지 핵심 */
}

/* ===================== 공지 텍스트 ===================== */
.notice-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;

  font-size: 13px;
  line-height: 1.4;
  color: var(--color-text-main);

  cursor: pointer;
  transition: color 0.15s ease;
}

.notice-text:hover {
  color: var(--color-primary);
  text-decoration: underline;
}

/* ===================== 사이트 목록 ===================== */
.notice-sites {
  margin-top: 10px;
  font-size: 12px;
  color: var(--color-primary);
  font-weight: 600;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ===================== 날짜 ===================== */
.notice-created-at {
  margin-top: 10px;
  font-size: 11px;
  color: var(--color-text-sub);
  text-align: right;
}

/* ===================== empty ===================== */
.notice-empty {
  margin-top: 12px;
  font-size: 13px;
  color: var(--color-text-sub);
  text-align: center;
  padding: 20px 10px;
  border: 1px dashed #d7dde5;
  border-radius: 10px;
  background: #fafbfd;
}

/* ===================== 버튼 ===================== */
.notice-nav-btn {
  align-self: center;
}

.slide-btn {
  width: 34px;
  height: 34px;
  border: 1px solid #d9dfe7;
  border-radius: 999px;
  background: #fff;
  color: #4b5563;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  transition: all 0.2s ease;
}

.slide-btn:hover {
  background-color: #f2f5f9;
  border-color: #c7d0db;
}

/* ===================== 인디케이터 ===================== */
.slide-indicators {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.notice-indicators {
  margin-top: 10px;
}

.indicator {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  border: none;
  background: #d1d5db;
  cursor: pointer;
  padding: 0;
}

.indicator.active {
  background: var(--color-primary);
}

/* ===================== 모달 ===================== */
.notice-modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.65);

  display: flex;
  align-items: center;
  justify-content: center;

  z-index: 9999;

  animation: fadeIn 0.2s ease;
}

.notice-modal-content {
  position: relative;

  width: 420px;
  max-width: 90vw;
  max-height: 80vh;

  overflow-y: auto;

  background: #fff;
  border-radius: 16px;
  padding: 20px 18px;

  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);

  animation: scaleIn 0.2s ease;
}

/* ===================== 모달 내부 ===================== */
.notice-modal-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 10px;
}

.notice-modal-text {
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-line;
  color: var(--color-text-main);
}

.notice-modal-meta {
  margin-top: 12px;
  font-size: 12px;
  color: var(--color-text-sub);
  text-align: right;
}

/* 닫기 버튼 */
.close-btn {
  position: absolute;
  top: 12px;
  right: 14px;

  border: none;
  background: none;
  font-size: 18px;
  cursor: pointer;

  color: #6b7280;
}

.close-btn:hover {
  color: #111827;
}

/* ===================== 애니메이션 ===================== */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes scaleIn {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
</style>